import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Shield, CreditCard, Calendar, Users, ChevronDown, ChevronUp, X } from 'lucide-react';

const courses = [
    {
        title: 'UI/UX Design with AI',
        price: '₹25,000',
        emi: '₹4,167/month',
        duration: '12 Weeks',
        seats: 30,
        seatsLeft: 12,
        features: [
            '6-module structured curriculum',
            'Figma + AI tools mastery',
            'Real client project simulation',
            'Portfolio & placement support',
            'Lifetime community access',
            'Certificate + LinkedIn credential',
            'Mock interviews & resume review',
            '1-on-1 mentorship sessions',
        ],
    },
    {
        title: 'App Development with AI',
        price: '₹35,000',
        emi: '₹5,833/month',
        duration: '14 Weeks',
        seats: 30,
        seatsLeft: 8,
        popular: true,
        features: [
            '6-phase structured curriculum',
            'Flutter/React Native + AI tools',
            'Real client project simulation',
            'App Store deployment guidance',
            'Lifetime community access',
            'Certificate + LinkedIn credential',
            'Portfolio & placement support',
            '1-on-1 mentorship sessions',
        ],
    },
];

const batches = [
    { course: 'UI/UX Design with AI', date: 'August 5, 2026', seatsLeft: 12, status: 'Filling Fast' },
    { course: 'App Development with AI', date: 'August 12, 2026', seatsLeft: 8, status: 'Filling Fast' },
    { course: 'UI/UX Design with AI', date: 'September 2, 2026', seatsLeft: 24, status: 'Open' },
    { course: 'App Development with AI', date: 'September 9, 2026', seatsLeft: 28, status: 'Open' },
];

const inclusions = [
    { icon: Users, title: 'Live Mentorship', desc: 'Weekly live sessions with industry mentors' },
    { icon: Shield, title: 'Placement Support', desc: 'Resume review, mock interviews, job referrals' },
    { icon: CreditCard, title: 'No-Cost EMI', desc: 'Split payments over 3-6 months, zero interest' },
    { icon: Calendar, title: 'Flexible Batches', desc: 'Weekday and weekend options available' },
];

const faqs = [
    { q: 'What payment methods do you accept?', a: 'We accept UPI, net banking, credit/debit cards, and EMI through our payment partners.' },
    { q: 'How does the no-cost EMI work?', a: 'You can split the course fee into 3-6 monthly installments with zero interest. Available for all major credit cards.' },
    { q: 'Is there a refund policy?', a: "Yes, we offer a 7-day money-back guarantee. If you're not satisfied within the first week, we'll refund 100% of your payment." },
    { q: 'Are there group discounts?', a: 'Yes, groups of 3+ get 10% off. Groups of 5+ get 15% off. Contact us for corporate team pricing.' },
    { q: 'When do I get the certificate?', a: 'You receive your certificate upon completing all modules and the capstone project.' },
];

const PricingBatches = () => {
    const [openIndex, setOpenIndex] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [form, setForm] = useState({ name: '', email: '', phone: '', qualification: '' });

    const courseNames = [...new Set(batches.map(b => b.course))];
    const availableDates = batches
        .filter(b => !selectedCourse || b.course === selectedCourse)
        .map(b => ({ date: b.date, seatsLeft: b.seatsLeft }));

    const openModal = (courseName) => {
        setSelectedCourse(courseName || '');
        setSelectedDate('');
        setForm({ name: '', email: '', phone: '', qualification: '' });
        setShowModal(true);
    };

    const handleFormChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = () => {
        if (!form.name || !form.email || !form.phone || !form.qualification || !selectedCourse || !selectedDate) {
            alert('Please fill in all fields.');
            return;
        }
        alert(`Thank you, ${form.name}! We'll contact you shortly about ${selectedCourse} (${selectedDate}).`);
        setShowModal(false);
    };

    return (
        <>
            <Helmet>
                <title>Pricing & Batches | PyrusMedia Academy</title>
                <meta name="description" content="View course fees, EMI options, upcoming batch dates, and seat availability for PyrusMedia Academy courses." />
            </Helmet>

            {/* Hero */}
            <section className="pt-32 pb-20 bg-white text-center relative overflow-hidden">
                <div className="absolute top-20 left-1/4 w-[400px] h-[400px] bg-[#bebd19]/5 rounded-full blur-[120px]" />
                <div className="container mx-auto px-4 relative z-10">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
                    >
                        Invest in Your Future<br />
                        <span className="text-[#bebd19]">— Flexible Payment Options</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl text-gray-500 max-w-2xl mx-auto"
                    >
                        Transparent pricing, no hidden fees. Pay in easy EMIs.
                    </motion.p>
                </div>
            </section>

            {/* Pricing Cards */}
            <Section className="bg-gray-50">
                <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    {courses.map((course, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.15 }}
                            className={`bg-white p-8 rounded-2xl border-2 transition-all ${
                                course.popular
                                    ? 'border-[#bebd19] shadow-lg shadow-[#bebd19]/10 relative'
                                    : 'border-gray-200 hover:border-[#bebd19]'
                            }`}
                        >
                            {course.popular && (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#bebd19] text-black text-xs font-bold px-4 py-1 rounded-full">
                                    Most Popular
                                </div>
                            )}

                            <h3 className="text-2xl font-bold text-gray-900 mb-2">{course.title}</h3>
                            <div className="flex items-baseline gap-2 mb-1">
                                <span className="text-4xl font-bold text-gray-900">{course.price}</span>
                                <span className="text-gray-500 text-sm">one-time</span>
                            </div>
                            <p className="text-sm text-[#bebd19] font-medium mb-6">or {course.emi} with no-cost EMI</p>

                            <div className="flex items-center gap-4 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-200">
                                <span className="flex items-center gap-1"><Calendar size={14} /> {course.duration}</span>
                                <span className="flex items-center gap-1"><Users size={14} /> {course.seatsLeft}/{course.seats} seats left</span>
                            </div>

                            <ul className="space-y-3 mb-8">
                                {course.features.map((f, j) => (
                                    <li key={j} className="flex items-start gap-3">
                                        <Check size={18} className="text-[#bebd19] shrink-0 mt-0.5" />
                                        <span className="text-gray-600 text-sm">{f}</span>
                                    </li>
                                ))}
                            </ul>

                            <Button
                                onClick={() => openModal(course.title)}
                                className={`w-full text-center ${course.popular ? '' : 'bg-gray-900 hover:bg-gray-800'}`}
                            >
                                Reserve Your Seat
                            </Button>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* What's Included */}
            <Section className="bg-white">
                <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">What's Included</h2>
                <div className="grid md:grid-cols-4 gap-6">
                    {inclusions.map((item, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="text-center p-6"
                        >
                            <div className="w-14 h-14 rounded-full bg-[#bebd19]/10 flex items-center justify-center mx-auto mb-4">
                                <item.icon size={24} className="text-[#bebd19]" />
                            </div>
                            <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                            <p className="text-sm text-gray-500">{item.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* Upcoming Batches */}
            <Section className="bg-gray-50">
                <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Upcoming Batches</h2>
                <div className="max-w-4xl mx-auto overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left py-4 px-4 text-gray-500 font-medium text-sm">Course</th>
                                <th className="text-left py-4 px-4 text-gray-500 font-medium text-sm">Start Date</th>
                                <th className="text-left py-4 px-4 text-gray-500 font-medium text-sm">Seats Left</th>
                                <th className="text-left py-4 px-4 text-gray-500 font-medium text-sm">Status</th>
                                <th className="text-right py-4 px-4 text-gray-500 font-medium text-sm"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {batches.map((batch, i) => (
                                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                                    <td className="py-4 px-4 font-medium text-gray-900">{batch.course}</td>
                                    <td className="py-4 px-4 text-gray-600">{batch.date}</td>
                                    <td className="py-4 px-4 text-gray-600">{batch.seatsLeft} seats</td>
                                    <td className="py-4 px-4">
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                            batch.status === 'Filling Fast'
                                                ? 'bg-orange-100 text-orange-700'
                                                : 'bg-green-100 text-green-700'
                                        }`}>
                                            {batch.status}
                                        </span>
                                    </td>
                                    <td className="py-4 px-4 text-right">
                                        <Button onClick={() => openModal(batch.course)} className="!py-1.5 !px-4 text-xs">
                                            Enroll Now
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* EMI & Guarantee */}
            <Section className="bg-white">
                <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    <div className="bg-gray-50 p-8 rounded-2xl border border-gray-200">
                        <CreditCard size={32} className="text-[#bebd19] mb-4" />
                        <h3 className="text-xl font-bold text-gray-900 mb-3">No-Cost EMI Available</h3>
                        <p className="text-gray-500 text-sm mb-4">
                            Split your course fee into 3-6 monthly installments. Zero interest, no hidden charges.
                            Available for all major credit cards.
                        </p>
                        <ul className="space-y-2 text-sm text-gray-600">
                            <li className="flex items-center gap-2"><Check size={16} className="text-[#bebd19]" /> 3-month EMI: ₹8,333/month (UI/UX) | ₹11,667/month (App Dev)</li>
                            <li className="flex items-center gap-2"><Check size={16} className="text-[#bebd19]" /> 6-month EMI: ₹4,167/month (UI/UX) | ₹5,833/month (App Dev)</li>
                            <li className="flex items-center gap-2"><Check size={16} className="text-[#bebd19]" /> Instant approval — no paperwork</li>
                        </ul>
                    </div>

                    <div className="bg-gray-50 p-8 rounded-2xl border border-gray-200">
                        <Shield size={32} className="text-[#bebd19] mb-4" />
                        <h3 className="text-xl font-bold text-gray-900 mb-3">7-Day Money Back Guarantee</h3>
                        <p className="text-gray-500 text-sm mb-4">
                            We're confident in our courses. If you're not satisfied within the first 7 days,
                            we'll refund 100% of your payment — no questions asked.
                        </p>
                        <ul className="space-y-2 text-sm text-gray-600">
                            <li className="flex items-center gap-2"><Check size={16} className="text-[#bebd19]" /> Full refund within 7 days</li>
                            <li className="flex items-center gap-2"><Check size={16} className="text-[#bebd19]" /> No questions asked</li>
                            <li className="flex items-center gap-2"><Check size={16} className="text-[#bebd19]" /> Refund processed within 5 business days</li>
                        </ul>
                    </div>
                </div>
            </Section>

            {/* FAQ */}
            <Section className="bg-gray-50">
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Pricing FAQs</h2>
                    <div className="space-y-3">
                        {faqs.map((faq, i) => (
                            <div key={i} className="bg-white rounded-xl border border-gray-200">
                                <button
                                    onClick={() => setOpenIndex(openIndex === i ? null : i)}
                                    className="w-full p-5 flex items-center justify-between text-left"
                                >
                                    <span className="font-semibold text-gray-900">{faq.q}</span>
                                    {openIndex === i ? <ChevronUp className="text-[#bebd19]" /> : <ChevronDown className="text-gray-400" />}
                                </button>
                                {openIndex === i && (
                                    <div className="px-5 pb-5 text-gray-500 border-t border-gray-200 pt-3">
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* CTA */}
            <Section className="bg-[#bebd19]">
                <div className="text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Ready to Get Started?</h2>
                    <p className="text-black/70 text-lg mb-8 max-w-xl mx-auto">
                        Limited seats available. Secure your spot today with easy EMI options.
                    </p>
                    <Button onClick={() => openModal('')} className="bg-black text-white hover:bg-gray-800">
                        Reserve Your Seat Now
                    </Button>
                </div>
            </Section>

            {/* Enrollment Modal */}
            <AnimatePresence>
                {showModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
                    >
                        {/* Backdrop */}
                        <div
                            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                            onClick={() => setShowModal(false)}
                        />

                        {/* Modal */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="relative bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-md p-8"
                        >
                            <button
                                onClick={() => setShowModal(false)}
                                className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 transition-colors"
                            >
                                <X size={24} />
                            </button>

                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Reserve Your Seat</h2>
                            <p className="text-gray-500 text-sm mb-6">Fill in your details to reserve your spot.</p>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Full Name *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={form.name}
                                        onChange={handleFormChange}
                                        placeholder="Enter your full name"
                                        className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:border-[#bebd19] focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Email Address *</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleFormChange}
                                        placeholder="Enter your email"
                                        className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:border-[#bebd19] focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Mobile Number *</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={form.phone}
                                        onChange={handleFormChange}
                                        placeholder="Enter your mobile number"
                                        className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:border-[#bebd19] focus:outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Qualification *</label>
                                    <select
                                        name="qualification"
                                        value={form.qualification}
                                        onChange={handleFormChange}
                                        className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:border-[#bebd19] focus:outline-none"
                                    >
                                        <option value="">Select your qualification</option>
                                        <option>High School</option>
                                        <option>Currently in College</option>
                                        <option>Graduate</option>
                                        <option>Post Graduate</option>
                                        <option>Working Professional</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Course *</label>
                                    <select
                                        value={selectedCourse}
                                        onChange={(e) => {
                                            setSelectedCourse(e.target.value);
                                            setSelectedDate('');
                                        }}
                                        className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:border-[#bebd19] focus:outline-none"
                                    >
                                        <option value="">Select a course</option>
                                        {courseNames.map(name => (
                                            <option key={name} value={name}>{name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-1">Start Date *</label>
                                    <select
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        disabled={!selectedCourse}
                                        className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:border-[#bebd19] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <option value="">Select a date</option>
                                        {availableDates.map((b, i) => (
                                            <option key={i} value={b.date}>
                                                {b.date} ({b.seatsLeft} seats left)
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 py-3 rounded-full border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    className="flex-1 py-3 rounded-full bg-[#bebd19] text-black font-bold hover:bg-[#d4d325] transition-colors"
                                >
                                    Confirm & Continue
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default PricingBatches;
